import { useMemo } from "react";
import { XIcon } from "icons/outline";

import { Button } from "ui";
import Drawer from "../Drawer";
import { parseErrorsToObject } from "../../utils/parseErrors";
import { useTestMode } from "../../contexts/TestModeProvider";
import {
  Form,
  FormField,
  FormLabel,
  FormErrorMessage,
  InputFormField,
  useFormState,
  DropdownSearchFormField,
  useFormContext,
  useWatch,
} from "../../features/form";
import { ErrorResponse } from "../../hooks/useMoneyHashQuery";
import type { Flow } from "./types";
import { EmptyAssignedAccounts } from "../EmptyAssignedAccounts";
import { getAccountsWithSearch } from "../../utils/dropdownsGetOptions";

type HandleSubmitData = { name: string; accounts: string[][] };
type SubmitData = {
  name: string;
  accounts: string[];
  type: "PayIn";
};
type DrawerFlowFormContentProps = {
  flowId?: string;
  onClose: () => void;
  initialFlowName?: string;
  initialAccounts?: string[][];
  onSubmit: (data: SubmitData & {}) => Promise<Flow>;
  onSuccess: (data: Flow) => void;
  submitBtnLabel: string;
};

export default function DrawerFlowFormContent({
  flowId,
  onClose,
  initialFlowName = "",
  initialAccounts = [],
  onSubmit,
  onSuccess,
  submitBtnLabel = "Create",
}: DrawerFlowFormContentProps) {
  const isEditMode = Boolean(flowId);
  const { isTestMode } = useTestMode();

  const handleSubmit = (data: HandleSubmitData) => {
    return onSubmit({
      name: data.name,
      accounts: data.accounts.map((account) => account[0]),
      type: "PayIn",
      ...(isEditMode ? {} : { isLive: !isTestMode }),
    })
      .then((flow) => {
        onSuccess?.(flow);
      })
      .catch((error: ErrorResponse) => {
        const errors = parseErrorsToObject(error);
        return {
          errors: { ...errors, root: "Something went wrong." },
        };
      });
  };

  const defaultValues = useMemo(
    () => ({
      name: initialFlowName,
      accounts: initialAccounts,
    }),
    [initialAccounts, initialFlowName]
  );

  return (
    <Form
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      className="flex flex-1 flex-col"
    >
      <Drawer.Content className="space-y-4 p-8 pb-0">
        <h2 className="text-sm font-medium uppercase text-foundations-boldest">
          Workflow Configuration
        </h2>

        <FlowForm isEditMode={isEditMode} flowId={flowId} />
      </Drawer.Content>

      <Drawer.Footer>
        <FormActions submitBtnLabel={submitBtnLabel} onClose={onClose} />
      </Drawer.Footer>
    </Form>
  );
}

const FlowForm = ({
  isEditMode,
  flowId,
}: {
  isEditMode: boolean;
  flowId?: string;
}) => {
  const selectedAccounts: [string, string][] | undefined = useWatch({
    name: "accounts",
  });

  return (
    <>
      <FormField label="Flow Name" name="name" required validation="required">
        <FormLabel />
        <InputFormField placeholder="Flow name" />
        <FormErrorMessage />
      </FormField>

      {!isEditMode && (
        <>
          <h2 className="!mt-8 text-sm font-medium uppercase text-foundations-boldest">
            Accounts
          </h2>

          <FormField
            label="Assign to accounts"
            name="accounts"
            required
            validation="required"
          >
            <FormLabel />
            <DropdownSearchFormField<string>
              multiple
              getOptions={(searchTerm) =>
                getAccountsWithSearch(searchTerm).then(
                  (res) =>
                    res.data.data.map(({ columns }) => [
                      columns.id,
                      columns.name,
                    ]) || []
                )
              }
              label="Select accounts"
              placeholder="Search accounts..."
            />
            <FormErrorMessage />
          </FormField>
        </>
      )}

      <AccountItems flowId={flowId} allAccounts={selectedAccounts || []} />
    </>
  );
};

const AccountItems = ({
  flowId,
}: {
  flowId?: string;
  allAccounts: [string, string][];
}) => {
  const { setValue } = useFormContext();
  const selectedAccounts: [string, string][] | undefined = useWatch({
    name: "accounts",
  });

  return (
    <>
      <p className="text-sm font-medium text-foundations-boldest">
        Assigned accounts:
      </p>
      {selectedAccounts?.length ? (
        <div className="flex max-h-96 flex-col space-y-4">
          {selectedAccounts?.map((account) => {
            const accountId = account?.[0];
            const accountName = account?.[1];

            if (!accountId || !accountName) return null;

            return (
              <AccountItem
                key={accountId}
                accountId={accountId}
                flowId={flowId}
                accountName={accountName}
                onDelete={(id) => {
                  const filteredAccounts = selectedAccounts?.filter(
                    ([acc]) => acc !== id
                  );

                  setValue("accounts", filteredAccounts);
                }}
              />
            );
          })}
        </div>
      ) : (
        <EmptyAssignedAccounts />
      )}
    </>
  );
};

const AccountItem = ({
  accountId,
  accountName,
  onDelete,
  flowId,
}: {
  accountId: string;
  accountName: string;
  onDelete: (accountId: string) => void;
  flowId?: string;
}) => {
  return (
    <div className="flex items-center space-x-2 text-sm text-foundations-boldest">
      <div className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-neutral-600 text-xs font-medium uppercase text-white ">
        {accountName?.slice(0, 2)}
      </div>
      <span className="flex-1 font-medium">{accountName}</span>
      {!flowId ? (
        <button
          type="button"
          className="text-foundations-subtle"
          onClick={() => onDelete(accountId)}
          aria-label="Delete account"
        >
          <XIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
};

const FormActions = ({
  submitBtnLabel,
  onClose,
}: {
  submitBtnLabel: string;
  onClose: () => void;
}) => {
  const { isSubmitting } = useFormState();

  return (
    <>
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>

      <Button type="submit" isLoading={isSubmitting}>
        {submitBtnLabel}
      </Button>
    </>
  );
};
